type SupportedCountry = 'AU' | 'NZ' | 'CA' | 'UK' | 'US' | 'DE' | 'EU' | 'OTHER';

/**
 * Infers the user's country from URL path (for Germany) and browser timezone/locale (for others).
 * Returns a supported country code for compliance logo display.
 *
 * Germany detection uses URL path (/de/) rather than timezone to ensure
 * the DSGVO badge is only shown to users explicitly browsing the German site.
 */
function useUserCountry(): SupportedCountry {
  if (typeof window === 'undefined') {
    return 'OTHER';
  }

  const pathname = window.location.pathname;
  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone || '';
  const locale = navigator.language || navigator.languages?.[0] || '';

  // Check for Germany based on URL path (for DSGVO compliance badge)
  // This ensures the badge is only shown when explicitly browsing the German site
  if (pathname.includes('/de/')) {
    return 'DE';
  }

  // Check timezone first as it's more reliable for location
  if (timezone && timezone.startsWith('Australia/')) {
    return 'AU';
  }
  if (
    timezone &&
    (timezone.startsWith('Pacific/Auckland') || timezone.startsWith('Pacific/Chatham'))
  ) {
    return 'NZ';
  }
  if (
    timezone &&
    (timezone.startsWith('America/Toronto') ||
      timezone.startsWith('America/Vancouver') ||
      timezone.startsWith('America/Montreal') ||
      timezone.startsWith('America/Edmonton') ||
      timezone.startsWith('America/Winnipeg') ||
      timezone.startsWith('America/Halifax') ||
      timezone.startsWith('America/St_Johns') ||
      timezone.startsWith('America/Regina') ||
      timezone.startsWith('America/Yellowknife') ||
      timezone.startsWith('America/Whitehorse'))
  ) {
    return 'CA';
  }
  if (
    timezone &&
    (timezone.startsWith('Europe/London') || timezone === 'Europe/Belfast')
  ) {
    return 'UK';
  }
  // Check for EU countries (excluding UK which is handled above)
  // Note: German timezones (Europe/Berlin, Europe/Busingen) fall into EU here;
  // DE is only returned when the user is on a /de/ path
  if (
    timezone &&
    timezone.startsWith('Europe/') &&
    !timezone.startsWith('Europe/London') &&
    timezone !== 'Europe/Belfast'
  ) {
    return 'EU';
  }
  // US timezones - check Pacific/Honolulu separately since it's not under America/
  if (timezone === 'Pacific/Honolulu') {
    return 'US';
  }
  if (
    timezone &&
    timezone.startsWith('America/') &&
    !timezone.startsWith('America/Toronto') &&
    !timezone.startsWith('America/Vancouver') &&
    !timezone.startsWith('America/Montreal') &&
    !timezone.startsWith('America/Edmonton') &&
    !timezone.startsWith('America/Winnipeg') &&
    !timezone.startsWith('America/Halifax') &&
    !timezone.startsWith('America/St_Johns') &&
    !timezone.startsWith('America/Regina') &&
    !timezone.startsWith('America/Yellowknife') &&
    !timezone.startsWith('America/Whitehorse')
  ) {
    // Most America/ timezones are US (excluding Canadian ones already checked)
    // This is a simplification - some Central/South American countries use America/ prefix
    const usTimezones = [
      'America/New_York',
      'America/Chicago',
      'America/Denver',
      'America/Los_Angeles',
      'America/Anchorage',
      'America/Phoenix',
      'America/Detroit',
      'America/Indiana',
      'America/Kentucky',
      'America/Boise',
      'America/Juneau',
      'America/Sitka',
      'America/Yakutat',
      'America/Nome',
      'America/Adak',
      'America/Menominee',
    ];
    if (usTimezones.some((tz) => timezone.startsWith(tz))) {
      return 'US';
    }
  }

  // If timezone is set but doesn't match any supported region, return OTHER
  // This prevents locale fallback from incorrectly matching users in unsupported regions
  // (e.g., Asia/Tehran with en-US locale should show OTHER, not US)
  if (timezone) {
    return 'OTHER';
  }

  // Fallback to locale check only when timezone is unavailable
  const localeUpper = locale.toUpperCase();
  if (localeUpper.includes('-AU') || localeUpper === 'EN-AU') {
    return 'AU';
  }
  if (localeUpper.includes('-NZ') || localeUpper === 'EN-NZ') {
    return 'NZ';
  }
  if (localeUpper.includes('-CA') || localeUpper === 'EN-CA' || localeUpper === 'FR-CA') {
    return 'CA';
  }
  if (localeUpper.includes('-GB') || localeUpper === 'EN-GB') {
    return 'UK';
  }
  if (localeUpper.includes('-US') || localeUpper === 'EN-US') {
    return 'US';
  }
  // Note: Germany locale fallback removed - DE is only returned via path-based detection
  // EU country locale codes (major EU member states, including Germany locales as EU fallback)
  const euLocaleCodes = [
    '-DE', // Germany (falls into EU when not on /de/ path)
    '-FR', // France
    '-ES', // Spain
    '-IT', // Italy
    '-NL', // Netherlands
    '-BE', // Belgium
    '-AT', // Austria
    '-PT', // Portugal
    '-PL', // Poland
    '-SE', // Sweden
    '-DK', // Denmark
    '-FI', // Finland
    '-IE', // Ireland
    '-GR', // Greece
    '-CZ', // Czech Republic
    '-RO', // Romania
    '-HU', // Hungary
    '-SK', // Slovakia
    '-BG', // Bulgaria
    '-HR', // Croatia
    '-LT', // Lithuania
    '-LV', // Latvia
    '-EE', // Estonia
    '-SI', // Slovenia
    '-LU', // Luxembourg
    '-MT', // Malta
    '-CY', // Cyprus
  ];
  // Also check for plain 'DE' locale (without country suffix)
  if (localeUpper === 'DE' || euLocaleCodes.some((code) => localeUpper.includes(code))) {
    return 'EU';
  }

  return 'OTHER';
}

export { useUserCountry };
export type { SupportedCountry };
